import {i18n, TFunction} from 'i18next';
import {NextPageContext, NextComponentType} from 'next';
import {AppContext, AppProps} from 'next/app';
import {ComponentType, CSSProperties, ReactElement} from 'react';
import {TransitionStatus} from 'react-transition-group/Transition';
import {Store} from 'redux';
import {Task} from 'redux-saga';
import {HomeTabPage} from '../../home/common/type';
import {BadgeVariant} from '../../ui/common/Badge';
import {Page} from '../constant/page';
import {IPageAppContext} from '../context';
import {IReduxState} from '../store';

export type ITheme = 'light' | 'dark';

export type IKwApp = ComponentType<IKwAppProps> & {
  getInitialProps: (
    context: IKwAppContext
  ) => Promise<Omit<IKwAppProps, 'Component' | 'router'>>;
};

export interface IKwAppContext extends Omit<AppContext, 'Component' | 'ctx'> {
  Component: IKwPage;
  ctx: INextKakaowebtoonContext;
}

export interface IKwAppProps extends Omit<AppProps, 'Component'> {
  Component: IKwPage;
  context: IPageAppContext;
  lang: string;
  theme: ITheme;
  serverContentAdultMap?: IAdultMap;
}

export type IKwPage<P = {}, Q = never> = NextComponentType<
  INextKakaowebtoonContext<Q>,
  P,
  P
> & {
  initialPageProps: IInitialPageProps;
};

export interface INextKakaowebtoonContext<Q = any>
  extends Omit<NextPageContext, 'query'> {
  query: Q;
  userAgent?: IUserAgent;
  store: PStore;
}

export interface IInitialPageProps extends ILayoutProps {
  getPageTitle: (i18next: i18n) => string;
  pageBgClassName?: string;
  zIndex?: number;
}

export interface PStore extends Store<IReduxState> {
  sagaTask?: Task;
}

export interface IUserAgent {
  isMobile: boolean;
  isWebView: boolean;
  version: string;
  osName: string;
  osVersion: string;
  rawData: string;
  // os
  isIos: boolean;
  isMacOs: boolean;
  isAndroid: boolean;
  // browser
  browserName: string;
  isChrome: boolean;
  isMsie: boolean;
  isSafari: boolean;
  isFirefox: boolean;
  isSamsungBrowser: boolean;
  deviceName: string;
}

export type Translator = (t: TFunction) => string;

export interface IEffectStyle {
  type: EffectType;
  defaultValue: string | number;
  value: {[key in TransitionStatus]?: number | string};
}

export enum EffectType {
  Transform = 'transform',
  Opacity = 'opacity',
  MarginTop = 'margin-top',
}
export const EffectTypeCamelMap: {[key in EffectType]: string} = {
  [EffectType.Transform]: 'transform',
  [EffectType.Opacity]: 'opacity',
  [EffectType.MarginTop]: 'marginTop',
};

export interface IContentMeta {
  id: number;
  sid: string;
  // TODO: 제거하자
  rid: string; // representationId
  title: string;
  titleImg: string;
  mainImg: string;
  mainTitleImg: string;
  decoImg?: string;
  thumbImg: string;
  anchorClip?: IAnchorClip;
  motionVideo?: VideoSource[]; // 캐릭터 모션 비디오
  motionStartImg?: string;
  motionEndImg?: string;
  bgImg: string;
  bgColor: string;
  bgColorRgb: string;
  badges: Badge[];
  genre: string;
  descLine3: string;
  descLine2: string;
  synopsis: string;
  seoKeywords: string[];
  authors: string;
  authorDetails: IAuthorDetails;
  promotionVideo?: PromotionVideo;
  videoClips: VideoClip[];
  viewCount: number;
  likeCount: number;
  ageLimit: number;
  copyright: string;
  isAdult: boolean;
  isStopContent: boolean;
  shareImg: string;
}

export interface Badge {
  variant?: BadgeVariant;
  label: string;
  waitfree?: BadgeWaitfree;
  imgType?: BadgeImgType;
  imgTextType?: BadgeImgTextType;
  isEllipsis?: boolean;
}

export interface BadgeWaitfree {
  hours: number;
}

export type BadgeImgType =
  | 'adultKor'
  | 'adult'
  | 'waitForFree'
  | 'waitForFreeTimer'
  | 'waitForFreeWaiting'
  | 'kakaopage';

export type BadgeImgTextType =
  | 'waitForFreePlus'
  | 'waitForFreePlusExtended'
  | 'waitForFreePlusTimer'
  | 'waitForFreeExtended'
  | 'waitForFreeNotice'
  | 'waitForFreeNoticeCharging';

export interface BadgeClassName {
  customTypeClassName?: string;
  variantTypeClassName?: string;
  imgTypeClassName?: string;
  imgTextTypeClassName?: string;
}

export interface Video {
  title?: string;
  subTitle?: string;
  sources: VideoSource[];
}

export interface PromotionVideo {
  title?: string;
  subTitle?: string;
  verticalSources: VideoSource[];
  horizontalSources: VideoSource[];
  thumbImg?: string;
}

export interface VideoSource {
  type: 'video/webm' | 'video/quicktime' | 'video/mp4';
  src: string;
}

export interface VideoClip {
  key: string;
  type: IExternalVideoType;
}

interface IAnchorClip {
  sources: VideoSource[];
  motionStartImg: string;
}

export type IExternalVideoType = 'kakaoTv' | 'youtube' | 'afreecaTv';

export type IAuthorDetails = Array<{
  type: IAuthorType;
  typeKey: string;
  names: string[];
}>;

export interface ILabel {
  text: string;
  bgColor?: string;
  bgColorRgb?: string;
}

export interface IFetchInfoItem<T> {
  [actionType: string]: {[key: string]: T};
}

export type IFetchStatus = 'request' | 'success' | 'fail';

export type TabItemVariant =
  | 'onlyWhiteUnderlineV4'
  | 'transparentWhite'
  | 'whiteCircleBg'
  | 'whiteCircleBgFourOrMoreItems'
  | 'underlineTransparent'
  | 'transparentWhiteSmall'
  | 'whiteUnderline';

export interface IToast {
  toastId?: string | number;
  title?: string;
  desc: string;
  showingDuration?: number;
  variant?: IToastVariant;
  src?: string;
  isShowEvery?: boolean;
}
export type IToastVariant = 'default' | 'black' | 'transparent';

export type IButtonIcon = 'close' | 'back' | 'notice' | 'newNotice';

export interface ILayoutProps {
  page: Page;
}

export interface IMaintenancePopup {
  title: string;
  subTitle: string;
  content: string;
}

export type ITicketType =
  | 'rental'
  | 'possession'
  | 'gift'
  | 'waitForFree'
  | 'welcomeGift';

export type IEpisodeSort = 'latest' | 'oldest';

export interface INotice {
  id: number;
  title: string;
  contents: string;
  agoText: string;
}

export type INoticeType = 'content' | 'notification' | 'faq';

export type IAdBannerType =
  | IMomenBannerType
  | 'adNormalEvent'
  | 'adSpecialEvent'
  | 'default';

export type IMomenBannerType = 'adMoment' | 'adBigMoment';

export type IAdBannerPage =
  | Page.Content
  | HomeTabPage.TimeTable
  | Page.Viewer
  | HomeTabPage.Ranking;

export type IAdBanner = IMomentBanner | IAdminBanner | IDefaultBanner;

interface IMomentBanner {
  id: string;
  isMoment: true;
  type: IMomenBannerType;
  page: IAdBannerPage;
  defaultBanner?: IAdminBannerComponent;
  placement: string;
  cardgroupId?: number;
}

interface IAdminBanner {
  id: string;
  isMoment: false;
  type: IAdBannerType;
  adminAd: IAdminBannerComponent;
  defaultBanner?: IAdminBannerComponent;
  placement: string;
  cardgroupId?: number;
}

interface IDefaultBanner {
  id: string;
  type: 'default';
  isMoment: false;
  defaultBanner?: IAdminBannerComponent;
  placement: string;
  cardgroupId?: number;
}

export interface IAdminBannerComponent {
  thumbImg: string;
  landingUri: string;
  bgColor: string;
  textColor: string;
  title: string;
  subTitle?: string;
  decorationImg?: string;
}

export interface IWebToAppBanner {
  image: string;
  landingUri: string;
}

// 참고: https://podotree.atlassian.net/wiki/spaces/KW/pages/1640765181/Service+Region+Language+Code
export type IRegion = 'kor' | 'twn' | 'tha' | 'idn';
export type ILanguage = 'ko' | 'en' | 'th' | 'zht' | 'id';

export type IAuthorType =
  | 'author'
  | 'illustrator'
  | 'originalStory'
  | 'adaptation'
  | 'color'
  | 'publisher';

export interface IUserCashInfo {
  purchasedCash: number;
  eventCash: number;
  expireInfo?: {
    cash: number;
    date: string;
  };
  autoPay: {
    isOn: boolean;
    benefitRatio: number;
  };
}

export interface ITextWithEllipsisItem {
  isEllipsis: boolean;
  text: string;
}

export type IAdultMap = {[id: number]: boolean | undefined};

export interface ITorosInfo {
  torosImpId: string;
  torosFileHashKey: string;
}

export type BadgeType =
  | 'episodesNotPublishing' // 휴재
  | 'episodesPublishing' // 연재
  | 'endOfSeason' // 시즌완결
  | 'seasonCompleted' // 시즌완결
  | 'completed' // 완결
  | 'stopSelling' // 판매중지
  | 'alive' // ALIVE
  | 'brand' // 브랜드웹툰
  | 'new' // 신작
  | 'season' // 새시즌
  | 'up' // UP
  | '12'
  | '15'
  | '19'
  | 'mon'
  | 'tue'
  | 'wed'
  | 'thu'
  | 'fri'
  | 'sat'
  | 'sun'
  | 'rental'
  | 'possession'
  | 'free'
  | 'waitForFree'
  | 'waitForFreePlus'
  | 'waitForFreePlusTimer'
  | 'waitForFreePlusExtended'
  | 'waitForFreeExtended'
  | 'waitForFreeTimer'
  | 'waitForFreeWaiting'
  | 'freePublishing'
  | 'event'
  | 'kakaopage'
  | 'pageOriginalNovel';

export interface ISetting {
  key: string;
  title: string;
  content: string;
  isEnabled: boolean;
}

export interface IAnchorCard
  extends Pick<
      IContentMeta,
      'id' | 'sid' | 'badges' | 'bgImg' | 'mainImg' | 'title'
    >,
    Partial<
      Pick<IContentMeta, 'mainTitleImg' | 'motionVideo' | 'motionStartImg'>
    > {}

// TODO: showContentCardInfo 제거하자.
export interface IContentCardCommonProps {
  additionalElement?:
    | ReactElement
    | ((setShowElement: (isLoaded: boolean) => void) => ReactElement);
  className?: string;
  thumbClassName?: string;
  titleImageClassName?: string;
  titleImageBottomElement?: ReactElement;
  badgeClassName?: string;
  showContentCardInfo?: boolean;
  isLazy?: boolean;
  index?: number;
  maskClassName?: string;
  maskStyle?: CSSProperties;
  beforeElement?: ReactElement;
  cardInfoClassName?: string;
  bgScaleClassName?: string;
  onClick?: (id: number, sid: string) => void;
  onIntersection?: (isIntersecting: boolean) => void;
  isBig?: boolean;
}
export interface IContentProps
  extends Pick<
      IContentMeta,
      'id' | 'sid' | 'title' | 'thumbImg' | 'titleImg' | 'badges'
    >,
    Partial<
      Pick<IContentMeta, 'bgImg' | 'isAdult' | 'bgColor' | 'bgColorRgb'>
    > {
  label?: ILabel;
}

export interface EnvVariables {
  WEB_HOST: string;
  WEB_STATIC_HOST: string;
  API_HOST: string;
  AUTH_HOST: string;
  BILLING_WEB_HOST: string;
  SENTRY_DSN: string;
  ENV_NAME: string;
  REGION: string;
  VERSION: string;
  TRACK_URL: string;
}
